import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/apiService';
import Banner from '../Banner/Banner';
import Button from '../Button/Button';
import ViewSwitch from '../ViewSwitch/ViewSwitch';
import CarteAffectation from './CarteAffectation';
import ListeAffectation from './ListeAffectation';
import { PALETTE, libelleDept } from './affectationColors';
import './AffectationCarte.css';

type Cote = 'ac' | 'libre';
interface LotTechnique { id: number; code?: string | null; nom: string; }
interface Affectable { id: number; nom: string; prenom: string; fonction?: string | null; email?: string | null; telephone?: string | null; }
interface DeptAffectation { departement_id: number; affecte_id: number | null; }
interface Contexte { cote: Cote; lots_techniques: LotTechnique[]; affectables: Affectable[]; }

const nomComplet = (a: Affectable): string => `${a.prenom ?? ''} ${a.nom ?? ''}`.trim() || `#${a.id}`;

// Libelle du lot dans le menu deroulant : "code - nom" (ou juste le nom si pas de code).
const libelleLot = (l: LotTechnique): string => (l.code ? `${l.code} - ${l.nom}` : l.nom);

/**
 * Section "Affectation des membres sur les lots". Un menu deroulant choisit le lot technique ;
 * panneau a gauche (action + roster), visualisation a droite (carte OU liste, au choix).
 * Selection multiple -> affectation en lot. Un departement a OBLIGATOIREMENT un interlocuteur.
 * Unifie AC (interlocuteurs) et Libre (membres) : le back resout le cote et la cible.
 */
const AffectationCarte: React.FC = () => {
  const [contexte, setContexte] = useState<Contexte | null>(null);
  const [loadingContexte, setLoadingContexte] = useState(true);
  const [lotId, setLotId] = useState<number | null>(null);
  const [affecteParDept, setAffecteParDept] = useState<Map<number, number | null>>(new Map());
  const [loadingLot, setLoadingLot] = useState(false);
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [vue, setVue] = useState<'carte' | 'liste'>('carte');
  const [survoleAffecte, setSurvoleAffecte] = useState<number | null>(null);
  const [deptFiltre, setDeptFiltre] = useState<number | null>(null);

  // Couleur + nom par affecte (legende + remplissage carte/liste).
  const couleurParAffecte = useMemo(() => {
    const m = new Map<number, string>();
    contexte?.affectables.forEach((a, i) => m.set(a.id, PALETTE[i % PALETTE.length]));
    return m;
  }, [contexte]);
  const nomParAffecte = useMemo(() => {
    const m = new Map<number, string>();
    contexte?.affectables.forEach((a) => m.set(a.id, nomComplet(a)));
    return m;
  }, [contexte]);
  // Coordonnees (fonction / email / tel) par affecte, pour l'infobulle de la carte.
  const coordParAffecte = useMemo(() => {
    const m = new Map<number, string>();
    contexte?.affectables.forEach((a) => {
      const c = [a.fonction, a.email, a.telephone].filter(Boolean).join(' · ');
      if (c) m.set(a.id, c);
    });
    return m;
  }, [contexte]);

  // Chargement du contexte (cote + lots + affectables) une fois.
  useEffect(() => {
    let annule = false;
    apiService.getAffectationContexte()
      .then((data: Contexte | null) => {
        if (annule || !data) return;
        setContexte(data);
        setLotId(data.lots_techniques[0]?.id ?? null);
      })
      .catch(() => { if (!annule) setErreur("Impossible de charger la carte d'affectation."); })
      .finally(() => { if (!annule) setLoadingContexte(false); });
    return () => { annule = true; };
  }, []);

  // Chargement de l'affectation du lot choisi.
  const chargerLot = useCallback((id: number) => {
    setLoadingLot(true);
    setErreur(null);
    apiService.getAffectationLot(id)
      .then((data: { departements: DeptAffectation[] } | null) => {
        const m = new Map<number, number | null>();
        (data?.departements ?? []).forEach((d) => m.set(d.departement_id, d.affecte_id));
        setAffecteParDept(m);
      })
      .catch(() => setErreur("Impossible de charger l'affectation de ce lot."))
      .finally(() => setLoadingLot(false));
  }, []);

  useEffect(() => {
    setSelection(new Set());
    if (lotId != null) chargerLot(lotId);
    else setAffecteParDept(new Map());
  }, [lotId, chargerLot]);

  const toggleDept = useCallback((deptId: number) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(deptId)) {
        next.delete(deptId);
      } else {
        next.add(deptId);
      }
      return next;
    });
  }, []);

  // Affecte les departements selectionnes a la personne choisie (affectation obligatoire).
  const appliquer = (affecteId: number) => {
    if (lotId == null || selection.size === 0) return;
    setSaving(true);
    setErreur(null);
    apiService.batchAffecterLot({ lot_technique_id: lotId, departement_ids: Array.from(selection), affecte_id: affecteId })
      .then((data: { departements: DeptAffectation[] } | null) => {
        if (data?.departements) {
          const m = new Map<number, number | null>();
          data.departements.forEach((d) => m.set(d.departement_id, d.affecte_id));
          setAffecteParDept(m);
        }
        setSelection(new Set());
      })
      .catch(() => setErreur("L'affectation a échoué. Vérifiez vos droits et réessayez."))
      .finally(() => setSaving(false));
  };

  if (loadingContexte) {
    return <p className="affectation-carte-info">Chargement de la carte…</p>;
  }
  if (!contexte || contexte.lots_techniques.length === 0) {
    return <p className="affectation-carte-info">Aucun lot technique à affecter pour le moment.</p>;
  }

  // Repartition des personnes + nombre de departements couverts par chacune.
  const comptesParAffecte = new Map<number, number>();
  affecteParDept.forEach((v) => {
    if (v != null) comptesParAffecte.set(v, (comptesParAffecte.get(v) ?? 0) + 1);
  });
  const affectes = contexte.affectables.filter((a) => comptesParAffecte.has(a.id));
  const nonAffectes = contexte.affectables.filter((a) => !comptesParAffecte.has(a.id));
  const labelCote = contexte.cote === 'ac' ? 'interlocuteur' : 'membre';
  const enSelection = selection.size > 0;
  const selectionTriee = Array.from(selection).sort((a, b) => a - b);

  // Departements du perimetre (liste + detection des non affectes a inciter).
  const deptsPerimetre = Array.from(affecteParDept.entries())
    .map(([departement_id, affecte_id]) => ({ departement_id, affecte_id }))
    .sort((a, b) => a.departement_id - b.departement_id);
  const deptsNonAffectes = deptsPerimetre.filter((d) => d.affecte_id == null).map((d) => d.departement_id);

  // Filtre departement (menu deroulant) : surligne la carte / restreint la liste (sans toucher le perimetre).
  const deptsDuLot = deptsPerimetre.map((d) => d.departement_id);
  const matchIds = deptFiltre != null ? new Set([deptFiltre]) : null;
  const deptsListe = matchIds ? deptsPerimetre.filter((d) => matchIds.has(d.departement_id)) : deptsPerimetre;

  // Une ligne du roster : pastille (couleur si affecte, creuse sinon) + nom + nb de departements.
  // Survol -> surligne ses departements (carte/liste) ; en selection, clic -> affecte la selection.
  const ligneAffectable = (a: Affectable, estAffecte: boolean) => (
    <button
      key={a.id}
      type="button"
      className={`affectation-roster-row${enSelection ? ' is-actionnable' : ''}`}
      onClick={() => { if (enSelection) appliquer(a.id); }}
      onMouseEnter={() => setSurvoleAffecte(a.id)}
      onMouseLeave={() => setSurvoleAffecte(null)}
      disabled={saving}
      title={enSelection ? `Affecter ${selection.size} département(s) à ${nomComplet(a)}` : 'Survolez pour voir ses départements'}
    >
      <span
        className={`affectation-roster-pastille${estAffecte ? '' : ' is-vide'}`}
        style={estAffecte ? { background: couleurParAffecte.get(a.id) } : undefined}
      />
      <span className="affectation-roster-nom">{nomComplet(a)}</span>
      {estAffecte && <span className="affectation-roster-count">{comptesParAffecte.get(a.id)} dép.</span>}
    </button>
  );

  return (
    <div className="affectation-carte">
      <Banner
        variant="info"
        className="affectation-carte-consigne-banner"
        message="Sélectionnez des départements, puis choisissez la personne dans le panneau de gauche."
      />

      <div className="affectation-carte-toolbar">
        <label className="affectation-carte-field">
          <span>Lot technique</span>
          <select value={lotId ?? ''} onChange={(e) => { setLotId(Number(e.target.value)); setDeptFiltre(null); }} disabled={saving}>
            {contexte.lots_techniques.map((l) => (
              <option key={l.id} value={l.id}>{libelleLot(l)}</option>
            ))}
          </select>
        </label>
        <label className="affectation-carte-field affectation-carte-field--filtre">
          <span>Département</span>
          <select value={deptFiltre ?? ''} onChange={(e) => setDeptFiltre(e.target.value ? Number(e.target.value) : null)} disabled={saving}>
            <option value="">Tous les départements</option>
            {deptsDuLot.map((id) => (
              <option key={id} value={id}>{libelleDept(id)}</option>
            ))}
          </select>
        </label>
        <ViewSwitch
          value={vue}
          options={[{ value: 'carte', label: 'Carte' }, { value: 'liste', label: 'Liste' }]}
          onChange={setVue}
          ariaLabel="Affichage"
        />
      </div>

      {erreur && <p className="affectation-carte-erreur" role="alert">{erreur}</p>}

      {loadingLot ? (
        <p className="affectation-carte-info">Chargement de l'affectation…</p>
      ) : (
        <div className="affectation-carte-board">
          {/* Panneau gauche : action / incitation + roster (survol -> surlignage) + legende. */}
          <div className="affectation-carte-panel">
            {enSelection ? (
              <Banner
                variant="info"
                hideIcon
                className="affectation-incite-banner"
                title={`${selection.size} département(s) sélectionné(s)`}
                message={(
                  <>
                    <span className="affectation-panel-chips">
                      {selectionTriee.map((id) => (
                        <span key={id} className="affectation-chip">
                          {libelleDept(id)}
                          <button
                            type="button"
                            className="affectation-chip-x"
                            onClick={() => toggleDept(id)}
                            disabled={saving}
                            aria-label={`Retirer ${libelleDept(id)}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </span>
                    <span className="affectation-panel-hint">Cliquez un {labelCote} ci-dessous pour l'affecter à ces départements.</span>
                  </>
                )}
                action={(
                  <Button
                    variant="secondary"
                    className="affectation-incite-action"
                    value="Tout désélectionner"
                    onClick={() => setSelection(new Set())}
                    disabled={saving}
                  />
                )}
              />
            ) : deptsNonAffectes.length > 0 ? (
              <Banner
                variant="warning"
                className="affectation-incite-banner"
                title={`${deptsNonAffectes.length} département(s) sans ${labelCote}`}
                message={`Chaque département doit avoir un ${labelCote}. Affectez ceux qui restent.`}
                action={(
                  <Button
                    variant="primary"
                    className="affectation-incite-action"
                    value={`Sélectionner les ${deptsNonAffectes.length} non affectés`}
                    onClick={() => setSelection(new Set(deptsNonAffectes))}
                    disabled={saving}
                  />
                )}
              />
            ) : (
              <Banner
                variant="success"
                className="affectation-incite-banner"
                message={`Tous les départements ont un ${labelCote}.`}
              />
            )}

            <div className="affectation-panel-roster">
              <span className="affectation-roster-titre">Avec départements</span>
              {affectes.length === 0
                ? <span className="affectation-roster-vide">Aucun pour ce lot</span>
                : affectes.map((a) => ligneAffectable(a, true))}
              <span className="affectation-roster-titre">Sans département</span>
              {nonAffectes.length === 0
                ? <span className="affectation-roster-vide">Aucun</span>
                : nonAffectes.map((a) => ligneAffectable(a, false))}
            </div>

            <div className="affectation-panel-etats">
              <span className="affectation-legende-item">
                <span className="affectation-legende-pastille affectation-legende-pastille--libre" /> Non affecté
              </span>
              <span className="affectation-legende-item">
                <span className="affectation-legende-pastille affectation-legende-pastille--hors" /> Hors périmètre
              </span>
            </div>
          </div>

          {/* Visualisation droite : carte OU liste (toggle). */}
          <div className="affectation-carte-visu">
            {vue === 'carte' ? (
              <div className="affectation-carte-mapcard">
                <CarteAffectation
                  affecteParDept={affecteParDept}
                  couleurParAffecte={couleurParAffecte}
                  nomParAffecte={nomParAffecte}
                  coordParAffecte={coordParAffecte}
                  selection={selection}
                  onToggle={toggleDept}
                  survoleAffecte={survoleAffecte}
                  surligneDepts={matchIds}
                  saving={saving}
                />
              </div>
            ) : (
              <ListeAffectation
                departements={deptsListe}
                libelleDept={libelleDept}
                couleurParAffecte={couleurParAffecte}
                nomParAffecte={nomParAffecte}
                selection={selection}
                onToggle={toggleDept}
                survoleAffecte={survoleAffecte}
                saving={saving}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AffectationCarte;
